import type {TranslateLogger} from '../../logger';
import type {ChatMessage, LLMClient} from './clients/types';

import {asyncify, eachLimit} from 'async';
import {dedent} from 'ts-dedent';

import {RateGate, backoff, estimateTokens} from './utils';

export type JudgePair = {
    path: string;
    source: string;
    translation: string;
};

export type JudgeVerdict = JudgePair & {
    score: number;
    issue: string;
};

export type JudgeParams = {
    client: LLMClient;
    pairs: JudgePair[];
    sourceLanguage: string;
    targetLanguage: string;
    maxBatchTokens: number;
    maxOutputTokens: number;
    maxConcurrency: number;
    retry: number;
    rateLimitRetry?: number;
    logger: TranslateLogger;
};

export type JudgeResult = {
    verdicts: JudgeVerdict[];
    /** Batches dropped entirely (request failed or response unparsable). */
    skippedBatches: number;
    /** Pairs left unscored: dropped batches plus indexes the judge omitted. */
    skippedPairs: number;
};

const JUDGE_SYSTEM_PROMPT = dedent`
    You are a strict reviewer of technical documentation translations.
    For every numbered pair below, score the translation from 0 (unusable) to 100 (perfect).

    Check, in order of importance:
    - meaning accuracy (no omissions, no additions);
    - Markdown/HTML/code and Liquid/YFM markup preserved exactly;
    - terminology consistency and correctness;
    - fluency in the target language.

    Respond with a JSON array only, no prose:
    [{"index": 1, "score": 95, "issue": ""}]
    One item per pair. "issue" is a short note explaining the deduction when score is below 100, otherwise an empty string.
`;

type JudgeScore = {
    index: number;
    score: number;
    issue?: string;
};

export function buildJudgeMessages(
    pairs: JudgePair[],
    sourceLanguage: string,
    targetLanguage: string,
): ChatMessage[] {
    const body = pairs
        .map(
            (pair, index) =>
                `[${index + 1}]\nSOURCE (${sourceLanguage}): ${pair.source}\nTRANSLATION (${targetLanguage}): ${pair.translation}`,
        )
        .join('\n\n');

    return [
        {role: 'system', content: JUDGE_SYSTEM_PROMPT},
        {role: 'user', content: body},
    ];
}

/**
 * Parses the judge response: a JSON array of {index, score, issue},
 * optionally wrapped in a markdown code fence. Returns null when the
 * response cannot be trusted (not JSON, wrong shape).
 */
export function parseJudgeResponse(text: string): JudgeScore[] | null {
    let payload = text.trim();

    const fence = /^```(?:json)?\n([\s\S]*?)\n```$/.exec(payload);
    if (fence) {
        payload = fence[1].trim();
    }

    try {
        const data = JSON.parse(payload);
        if (!Array.isArray(data)) {
            return null;
        }

        const scores: JudgeScore[] = [];
        for (const item of data) {
            if (
                !item ||
                typeof item.index !== 'number' ||
                typeof item.score !== 'number' ||
                !Number.isFinite(item.score)
            ) {
                return null;
            }
            scores.push({
                index: item.index,
                score: Math.max(0, Math.min(100, Math.round(item.score))),
                issue: typeof item.issue === 'string' ? item.issue : '',
            });
        }

        return scores;
    } catch {
        return null;
    }
}

function dedupePairs(pairs: JudgePair[]): JudgePair[] {
    const seen = new Set<string>();
    const unique: JudgePair[] = [];

    for (const pair of pairs) {
        const key = pair.source + ' ' + pair.translation;
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(pair);
        }
    }

    return unique;
}

function batchPairs(pairs: JudgePair[], maxBatchTokens: number): JudgePair[][] {
    const batches: JudgePair[][] = [];
    let batch: JudgePair[] = [];
    let budget = 0;

    for (const pair of pairs) {
        const tokens = estimateTokens(pair.source) + estimateTokens(pair.translation);
        if (budget + tokens > maxBatchTokens && batch.length) {
            batches.push(batch);
            batch = [];
            budget = 0;
        }
        batch.push(pair);
        budget += tokens;
    }

    if (batch.length) {
        batches.push(batch);
    }

    return batches;
}

/**
 * Scores source/translation pairs with a judge model.
 *
 * Best-effort by design: an unparsable or failed batch is logged and
 * skipped, quality evaluation never fails the translation run.
 */
export async function judgeTranslations(params: JudgeParams): Promise<JudgeResult> {
    const {client, pairs, sourceLanguage, targetLanguage, logger} = params;
    const {maxBatchTokens, maxOutputTokens, maxConcurrency, retry, rateLimitRetry} = params;

    const batches = batchPairs(dedupePairs(pairs), maxBatchTokens);
    const verdicts: JudgeVerdict[] = [];
    // One gate per run: a 429 from any batch pauses all of them.
    const gate = new RateGate();
    let skippedBatches = 0;
    let skippedPairs = 0;

    await eachLimit(
        batches,
        maxConcurrency,
        asyncify(async (batch: JudgePair[]) => {
            const messages = buildJudgeMessages(batch, sourceLanguage, targetLanguage);

            try {
                logger.request('judge', `${batch.length} pairs`);
                const result = await backoff(
                    () => client.complete(messages, {temperature: 0, maxTokens: maxOutputTokens}),
                    retry,
                    {rateLimitRetries: rateLimitRetry, gate},
                );

                const scores = parseJudgeResponse(result.text);
                if (!scores) {
                    skippedBatches++;
                    skippedPairs += batch.length;
                    logger.warn('judge', 'Unparsable judge response, batch skipped.');
                    return;
                }

                let scored = 0;
                for (const {index, score, issue} of scores) {
                    const pair = batch[index - 1];
                    if (pair) {
                        verdicts.push({...pair, score, issue: issue || ''});
                        scored++;
                    }
                }
                skippedPairs += Math.max(0, batch.length - scored);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                skippedBatches++;
                skippedPairs += batch.length;
                logger.warn('judge', `Failed to score a batch: ${error.message}`);
            }
        }),
    );

    return {verdicts, skippedBatches, skippedPairs};
}
